import React, { useState, useEffect, useMemo } from "react";
import {
  Button, Tag, Typography, Space, Empty, Skeleton, Alert,
  Modal, Input, Divider,
} from "antd";
import {
  DeleteOutlined, CheckCircleOutlined, InfoCircleOutlined,
  EyeOutlined, MergeCellsOutlined, SwapOutlined,
} from "@ant-design/icons";
import { fetchDuplicateOrders, fetchOrderByID, removeOrders } from "../../utils/fetchOrders";
import { mergeOrders } from "../../utils/fetchMerge";
import OrderDrawer from "./OrderDrawer";

const RouteStep2 = ({ orders, routeDay, onBack, onComplete }) => {
  const [localOrders, setLocalOrders] = useState(() => orders);
  const [rawGroups, setRawGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toRemove, setToRemove] = useState(new Set());

  // Drawer
  const [drawerOrderId, setDrawerOrderId] = useState(null);

  // Merge — step 1: picking source (inline in group)
  // selectingSource: { order, groupName } | null
  const [selectingSource, setSelectingSource] = useState(null);

  // Merge — step 2: confirmation modal
  // pendingMerge: { source: order, target: order } | null
  const [pendingMerge, setPendingMerge] = useState(null);
  const [mergeComment, setMergeComment] = useState("");
  const [mergeManual, setMergeManual] = useState({});
  const [isMerging, setIsMerging] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await fetchDuplicateOrders(routeDay, orders.map((o) => o.order_id));
        setRawGroups(data);
      } catch (err) {
        if (err.response?.status === 404) setRawGroups([]);
        else setError(err);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [routeDay]);

  const selectedIds = useMemo(
    () => new Set(localOrders.map((o) => o.order_id)),
    [localOrders]
  );

  const duplicateGroups = useMemo(() =>
    rawGroups
      .map((group) => ({
        ...group,
        orders: group.orders.filter((o) => selectedIds.has(o.order_id)),
      }))
      .filter((group) => group.orders.length >= 2),
    [rawGroups, selectedIds]
  );

  // ── toRemove toggle ────────────────────────────────────────────────────────
  const toggleRemove = (orderId) =>
    setToRemove((prev) => {
      const next = new Set(prev);
      next.has(orderId) ? next.delete(orderId) : next.add(orderId);
      return next;
    });

  // ── Merge: source selection ────────────────────────────────────────────────
  const startSelectingSource = (e, order, groupName) => {
    e.stopPropagation();
    setSelectingSource({ order, groupName });
  };

  const cancelSelectingSource = (e) => {
    e?.stopPropagation();
    setSelectingSource(null);
  };

  const pickTarget = (e, targetOrder) => {
    e.stopPropagation();
    setPendingMerge({ source: selectingSource.order, target: targetOrder });
    setSelectingSource(null);
    setMergeComment("");
    setMergeManual({});
  };

  // ── Merge: confirmation modal ──────────────────────────────────────────────
  const swapMergeDirection = () =>
    setPendingMerge((prev) => ({ source: prev.target, target: prev.source }));

  const cancelMerge = () => {
    setPendingMerge(null);
    setMergeComment("");
    setMergeManual({});
  };

  const confirmMerge = async () => {
    if (!pendingMerge) return;
    const { source, target } = pendingMerge;
    setIsMerging(true);
    try {
      await mergeOrders({
        order1_id: source.order_id,
        order2_id: target.order_id,
        delivery_date: routeDay,
        comment: mergeComment || undefined,
        manual_changes: mergeManual,
      });
      const updatedTarget = await fetchOrderByID(target.order_id);
      setLocalOrders((prev) =>
        prev
          .filter((o) => o.order_id !== source.order_id)
          .map((o) =>
            o.order_id === target.order_id ? { ...o, ...updatedTarget } : o
          )
      );
      setToRemove((prev) => {
        const next = new Set(prev);
        next.delete(source.order_id);
        return next;
      });
      cancelMerge();
    } catch (err) {
      Modal.error({
        title: "Error al fusionar",
        content: err.response?.data?.message ?? "No se pudo fusionar. Intenta de nuevo.",
      });
    } finally {
      setIsMerging(false);
    }
  };

  const handleContinue = async () => {
    const remainingOrders = localOrders.filter((o) => !toRemove.has(o.order_id));
    if (toRemove.size === 0) {
      onComplete(remainingOrders);
      return;
    }
    setIsRemoving(true);
    try {
      await removeOrders(Array.from(toRemove));
      onComplete(remainingOrders);
    } catch (err) {
      Modal.error({
        title: "Error al eliminar pedidos",
        content: err.response?.data?.message ?? "No se pudo eliminar. Intenta de nuevo.",
      });
    } finally {
      setIsRemoving(false);
    }
  };

  // ── Helpers ────────────────────────────────────────────────────────────────
  const isSelectingMode = selectingSource !== null;
  const isSourceOf = (orderId) => selectingSource?.order.order_id === orderId;
  const isActiveGroup = (groupName) => selectingSource?.groupName === groupName;

  const remaining = localOrders.length - toRemove.size;

  // ── Early returns ──────────────────────────────────────────────────────────
  if (isLoading) return <Skeleton active paragraph={{ rows: 6 }} />;

  if (error)
    return (
      <div>
        <Alert
          type="error"
          message="Error al cargar duplicados"
          description="No se pudo consultar el servidor. Puedes continuar de todas formas."
          className="mb-4"
        />
        <div className="flex justify-between">
          <Button onClick={onBack}>Volver</Button>
          <Button type="primary" onClick={() => onComplete(localOrders)}>
            Continuar sin revisar ({localOrders.length} pedidos)
          </Button>
        </div>
      </div>
    );

  if (duplicateGroups.length === 0)
    return (
      <div>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No hay pedidos con el mismo nombre en la selección"
        />
        <div className="flex justify-between mt-6">
          <Button onClick={onBack}>Volver</Button>
          <Button type="primary" onClick={() => onComplete(localOrders)}>
            Continuar ({localOrders.length} pedidos)
          </Button>
        </div>
      </div>
    );

  return (
    <div>
      <Alert
        type="warning"
        icon={<InfoCircleOutlined />}
        showIcon
        message={`${duplicateGroups.length} grupo${duplicateGroups.length > 1 ? "s" : ""} con nombres repetidos`}
        description="Mismo nombre no garantiza duplicado. Puedes eliminar, fusionar o mantener cada pedido."
        className="mb-4"
      />

      <div className="flex flex-col gap-3">
        {duplicateGroups.map((group, gi) => {
          const groupIsActive = isActiveGroup(group.name);
          return (
            <div
              key={gi}
              className={`border rounded-lg overflow-hidden transition-opacity ${
                isSelectingMode && !groupIsActive
                  ? "border-gray-200 dark:border-gray-700 opacity-40 pointer-events-none"
                  : "border-gray-200 dark:border-gray-700"
              }`}
            >
              {/* Group header */}
              <div className="bg-gray-50 dark:bg-gray-800 px-4 py-2 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                <Typography.Text strong>{group.name}</Typography.Text>
                <Space size={6}>
                  {groupIsActive && (
                    <Typography.Text type="warning" className="text-xs">
                      Selecciona el pedido destino
                    </Typography.Text>
                  )}
                  <Typography.Text type="secondary" className="text-sm">
                    {group.orders.length} pedidos
                  </Typography.Text>
                  {groupIsActive && (
                    <Button size="small" onClick={cancelSelectingSource}>
                      Cancelar
                    </Button>
                  )}
                </Space>
              </div>

              {/* Orders */}
              {group.orders.map((order) => {
                const isSource = isSourceOf(order.order_id);
                const isTargetable = groupIsActive && !isSource;
                const isRemoved = toRemove.has(order.order_id);

                let rowBg = "";
                if (isSource) rowBg = "bg-amber-50 dark:bg-amber-950/30";
                else if (isRemoved) rowBg = "bg-red-50 dark:bg-red-950/30";
                else if (isTargetable) rowBg = "hover:bg-blue-50 dark:hover:bg-blue-950/20 cursor-pointer";
                else if (!isSelectingMode) rowBg = "hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer";

                const deliveries = order.delivery_info_array ?? [];

                return (
                  <div
                    key={order.order_id}
                    onClick={
                      isTargetable
                        ? (e) => pickTarget(e, order)
                        : !isSelectingMode
                        ? () => toggleRemove(order.order_id)
                        : undefined
                    }
                    className={`px-4 py-3 transition-colors border-b border-gray-100 dark:border-gray-700 last:border-0 ${rowBg}`}
                  >
                    {/* Row 1: id + name + actions */}
                    <div className="flex justify-between items-start mb-1">
                      <Space size={6} wrap>
                        <Tag className="font-mono">{order.daily_id}</Tag>
                        <Typography.Text strong>{order.name}</Typography.Text>
                      </Space>

                      <Space size={4} onClick={(e) => e.stopPropagation()}>
                        {/* Eye icon — always */}
                        <Button
                          icon={<EyeOutlined />}
                          size="small"
                          type="text"
                          onClick={() => setDrawerOrderId(order.order_id)}
                        />

                        {isTargetable ? (
                          <Button
                            size="small"
                            type="primary"
                            icon={<MergeCellsOutlined />}
                            onClick={(e) => pickTarget(e, order)}
                          >
                            Fusionar aquí
                          </Button>
                        ) : isSource ? (
                          <Tag color="warning">Seleccionando destino…</Tag>
                        ) : !isSelectingMode ? (
                          <>
                            <Button
                              size="small"
                              icon={<MergeCellsOutlined />}
                              onClick={(e) => startSelectingSource(e, order, group.name)}
                            >
                              Fusionar
                            </Button>
                            {isRemoved ? (
                              <Tag icon={<DeleteOutlined />} color="error">Eliminar</Tag>
                            ) : (
                              <Tag icon={<CheckCircleOutlined />} color="success">Mantener</Tag>
                            )}
                          </>
                        ) : null}
                      </Space>
                    </div>

                    {/* Row 2: address */}
                    <Typography.Text type="secondary" className="text-xs block mb-1">
                      {[order.address, order.city?.toUpperCase()].filter(Boolean).join(" — ")}
                    </Typography.Text>

                    {/* Row 3: products */}
                    {order.products?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-1">
                        {order.products.map((p, i) => (
                          <Tag key={i} style={{ fontSize: 11, padding: "0 5px" }}>
                            {p.quantity}× {p.product_name}
                            {p.variant ? ` (${p.variant})` : ""}
                          </Tag>
                        ))}
                      </div>
                    )}

                    {/* Row 4: deliveries */}
                    {deliveries.length > 0 && (
                      <div className="flex flex-wrap items-center gap-1 mt-1">
                        <Typography.Text type="secondary" className="text-xs">
                          {deliveries.length} entrega{deliveries.length > 1 ? "s" : ""}:
                        </Typography.Text>
                        {deliveries.map((d, i) => (
                          <Tag
                            key={i}
                            color={d.driver_id ? "blue" : "default"}
                            style={{ fontSize: 11, padding: "0 5px" }}
                          >
                            {d.delivery_date?.slice(0, 10) ?? "—"}
                            {d.driver_name ? ` · ${d.driver_name}` : ""}
                          </Tag>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Bottom nav */}
      <div className="flex justify-between items-center mt-6">
        <Button onClick={onBack}>Volver</Button>
        <Space>
          {toRemove.size > 0 && (
            <Typography.Text type="secondary">
              {toRemove.size} pedido{toRemove.size > 1 ? "s" : ""} a eliminar
            </Typography.Text>
          )}
          <Button type="primary" loading={isRemoving} onClick={handleContinue}>
            Continuar ({remaining} pedidos)
          </Button>
        </Space>
      </div>

      {/* Order drawer */}
      <OrderDrawer orderId={drawerOrderId} onClose={() => setDrawerOrderId(null)} />

      {/* Merge confirmation modal */}
      <Modal
        open={pendingMerge !== null}
        title="Fusionar pedidos"
        onCancel={cancelMerge}
        footer={[
          <Button key="cancel" onClick={cancelMerge}>
            Cancelar
          </Button>,
          <Button
            key="confirm"
            type="primary"
            danger
            loading={isMerging}
            icon={<MergeCellsOutlined />}
            onClick={confirmMerge}
          >
            Fusionar
          </Button>,
        ]}
        width={520}
      >
        {pendingMerge && (
          <div>
            {/* Direction display */}
            <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg mb-4">
              <div className="flex-1 text-center">
                <Tag color="error" className="mb-2 block">Se elimina</Tag>
                <Tag className="font-mono text-sm">{pendingMerge.source.daily_id}</Tag>
                <Typography.Text className="block mt-1 text-sm">
                  {pendingMerge.source.name}
                </Typography.Text>
              </div>

              <div className="flex flex-col items-center gap-2 shrink-0">
                <Typography.Text className="text-xl">→</Typography.Text>
                <Button
                  icon={<SwapOutlined />}
                  size="small"
                  onClick={swapMergeDirection}
                  title="Intercambiar dirección"
                />
              </div>

              <div className="flex-1 text-center">
                <Tag color="success" className="mb-2 block">Sobrevive</Tag>
                <Tag className="font-mono text-sm">{pendingMerge.target.daily_id}</Tag>
                <Typography.Text className="block mt-1 text-sm">
                  {pendingMerge.target.name}
                </Typography.Text>
              </div>
            </div>

            <Typography.Text type="secondary" className="text-xs block mb-4">
              Los productos de <strong>{pendingMerge.source.daily_id}</strong> se añaden a{" "}
              <strong>{pendingMerge.target.daily_id}</strong>. La nota de entrega se escribe
              automáticamente, o puedes personalizarla abajo.
            </Typography.Text>

            {/* Comment */}
            <div className="mb-4">
              <Typography.Text className="text-sm block mb-1">
                Comentario en la entrega (opcional)
              </Typography.Text>
              <Input.TextArea
                placeholder={`Pedido incluye pedido ${pendingMerge.source.daily_id} y ${pendingMerge.target.daily_id}`}
                value={mergeComment}
                onChange={(e) => setMergeComment(e.target.value)}
                autoSize={{ minRows: 2, maxRows: 3 }}
              />
            </div>

            <Divider>
              <Typography.Text type="secondary" className="text-xs">
                Editar pedido sobreviviente (opcional)
              </Typography.Text>
            </Divider>

            <div className="grid grid-cols-2 gap-x-3 gap-y-2">
              {[
                { key: "name", label: "Nombre", placeholder: pendingMerge.target.name },
                { key: "address", label: "Dirección", placeholder: pendingMerge.target.address },
                { key: "city", label: "Ciudad", placeholder: pendingMerge.target.city },
                { key: "phone_number", label: "Teléfono", placeholder: pendingMerge.target.phone_number },
              ].map(({ key, label, placeholder }) => (
                <div key={key}>
                  <Typography.Text className="text-xs block mb-1">{label}</Typography.Text>
                  <Input
                    size="small"
                    placeholder={placeholder ?? "—"}
                    value={mergeManual[key] ?? ""}
                    onChange={(e) =>
                      setMergeManual((prev) => ({
                        ...prev,
                        [key]: e.target.value || undefined,
                      }))
                    }
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default RouteStep2;
